import { useEffect, useState } from "react";

import {
    DeliveryConditionsDto,
    DeliverySettingsDto,
    DeliverySettingsToAddressDto,
    DeliverySettingsTakeAwayToTimeDto,
    DeliverySettingsToRestaurantTableDto,
    DeliveryTypeDto,
    MenuSettingsDto,
} from "services/menu/restModels";

type DeliveryByType = Record<DeliveryTypeDto, DeliverySettingsDto>;

/*
* Способ заказа, которого у меню ещё не было, в ответе не придёт — но включить его
* форма должна уметь. Поэтому держим полный набор из шести, заполняя пропуски дефолтами.
* */
const buildDefaults = (currencyId: string): DeliveryByType => ({
    InsideTheRestaurant: {
        type: "InsideTheRestaurant",
        isEnabled: false,
        description: {},
    },
    ToRestaurantTable: {
        type: "ToRestaurantTable",
        isEnabled: false,
        description: {},
        canChooseSeat: false,
        isChangeTableEnabled: true,
    },
    TakeAway: {
        type: "TakeAway",
        isEnabled: false,
        description: {},
    },
    TakeAwayToTime: {
        type: "TakeAwayToTime",
        isEnabled: false,
        description: {},
        isDateRequired: false,
    },
    ToAddress: {
        type: "ToAddress",
        isEnabled: false,
        description: {},
        deliveryConditions: {
            type: "FixedPrice",
            fixedDeliveryPrice: { currencyId, price: 0 },
            freeDeliveryMinimalOrderPrice: { currencyId, price: 0 },
        },
    },
    ToVehicle: {
        type: "ToVehicle",
        isEnabled: false,
        description: {},
    },
});

export const useDeliveryState = (settings: MenuSettingsDto | null, currencyId: string) => {
    const [byType, setByType] = useState<DeliveryByType>(() => buildDefaults(currencyId));

    useEffect(() => {
        if (!settings) return;

        const next = buildDefaults(currencyId);
        settings.deliverySettings.forEach((item) => {
            next[item.type] = item;
        });
        setByType(next);
    }, [settings, currencyId]);

    const setEnabled = (type: DeliveryTypeDto, isEnabled: boolean) =>
        setByType(
            (prev) => ({ ...prev, [type]: { ...prev[type], isEnabled } } as DeliveryByType)
        );

    const setLocalizedDescription = (type: DeliveryTypeDto, value: string, lang: string) =>
        setByType(
            (prev) =>
                ({
                    ...prev,
                    [type]: {
                        ...prev[type],
                        description: { ...prev[type].description, [lang]: value },
                    },
                } as DeliveryByType)
        );

    const setCanChooseSeat = (canChooseSeat: boolean) =>
        setByType((prev) => ({
            ...prev,
            ToRestaurantTable: {
                ...(prev.ToRestaurantTable as DeliverySettingsToRestaurantTableDto),
                canChooseSeat,
            },
        }));

    const setIsChangeTableEnabled = (isChangeTableEnabled: boolean) =>
        setByType((prev) => ({
            ...prev,
            ToRestaurantTable: {
                ...(prev.ToRestaurantTable as DeliverySettingsToRestaurantTableDto),
                isChangeTableEnabled,
            },
        }));

    const setIsDateRequired = (isDateRequired: boolean) =>
        setByType((prev) => ({
            ...prev,
            TakeAwayToTime: {
                ...(prev.TakeAwayToTime as DeliverySettingsTakeAwayToTimeDto),
                isDateRequired,
            },
        }));

    const setDeliveryConditions = (deliveryConditions: DeliveryConditionsDto) =>
        setByType((prev) => ({
            ...prev,
            ToAddress: {
                ...(prev.ToAddress as DeliverySettingsToAddressDto),
                deliveryConditions,
            },
        }));

    return {
        deliveryByType: byType,
        deliveryList: Object.keys(byType).map((key) => byType[key as DeliveryTypeDto]),
        toAddress: byType.ToAddress as DeliverySettingsToAddressDto,
        toRestaurantTable: byType.ToRestaurantTable as DeliverySettingsToRestaurantTableDto,
        takeAwayToTime: byType.TakeAwayToTime as DeliverySettingsTakeAwayToTimeDto,
        setEnabled,
        setLocalizedDescription,
        setCanChooseSeat,
        setIsChangeTableEnabled,
        setIsDateRequired,
        setDeliveryConditions,
    };
};

export type DeliveryState = ReturnType<typeof useDeliveryState>;
